import { categoryLabel } from "@/lib/categories";
import React, { useEffect, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { BottomNav } from "@/components/BottomNav";
import { EmptyState } from "@/components/EmptyState";
import { QuantityStepper } from "@/components/QuantityStepper";
import { ProductImage } from "@/components/ProductImage";
import { MainAction } from "@/components/MainAction";
import { PromoBox } from "@/components/PromoBox";
import { toneFor } from "@/components/ProductCard";
import { AddOnRail, FreeDeliveryProgress } from "@/components/CartExtras";
import { useConfig } from "@/lib/useConfig";
import { mediaUrl } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useCart } from "@/state/CartContext";
import { useRouter } from "@/router/Router";
import { formatUzs } from "@/lib/format";
import { validatePromo } from "@/lib/api";
import { promoErrorText } from "@/lib/promoText";
import type { PromoPreview } from "@/types";

export function CartPage() {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const { lines, linesWithProducts, subtotalUzs, updateQuantity, removeItem, promoCode, bundleLines, bundleLinesWithData, updateBundleQuantity } = useCart();
  const { delivery } = useConfig();
  const [preview, setPreview] = useState<PromoPreview | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);

  const isEmpty = linesWithProducts.length === 0 && bundleLinesWithData.length === 0;

  // Live preview of the applied code. Validated as if delivery were chosen,
  // so a free-delivery code previews correctly; the final check happens at
  // checkout with the real delivery method.
  useEffect(() => {
    if (!promoCode || (lines.length === 0 && bundleLines.length === 0)) {
      setPreview(null);
      setPromoError(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      validatePromo(promoCode, lines, "yandex", bundleLines)
        .then((p) => {
          if (cancelled) return;
          setPreview(p);
          setPromoError(null);
        })
        .catch((err) => {
          if (cancelled) return;
          setPreview(null);
          setPromoError(promoErrorText(t, locale, err));
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [promoCode, lines, bundleLines, t, locale]);

  const goodsDiscount = preview && preview.type !== "free_delivery" ? preview.discountUzs : 0;

  return (
    <>
      <TopBar title={t("cart.title")} showCart={false} />
      <main className={isEmpty ? "page page--no-nav" : "page"}>
        {isEmpty ? (
          <EmptyState
            art="cart"
            title={t("cart.empty")}
            hint={t("cart.emptyHint")}
            action={
              <button className="btn btn--primary" onClick={() => navigate({ name: "catalog" })}>
                {t("cart.goToCatalog")}
              </button>
            }
          />
        ) : (
          <>
            <FreeDeliveryProgress subtotal={subtotalUzs} threshold={delivery.freeThresholdUzs} />
            <div style={{ marginBottom: "var(--space-4)" }}>
              {bundleLinesWithData.map((line) => (
                <div key={line.bundleId} className="cart-line">
                  <div className="cart-line__img cart-line__img--bundle">
                    {line.bundle.image ? (
                      <img src={mediaUrl(line.bundle.image)} alt="" />
                    ) : (
                      line.bundle.items.slice(0, 2).map((i) => <ProductImage key={i.variantId} src={i.image ?? undefined} alt="" className="product-card__image" />)
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="cart-line__title">{line.bundle.title}</div>
                    <div className="cart-line__meta">{t("bundles.cartMeta", { count: line.bundle.items.length })}</div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <QuantityStepper quantity={line.quantity} max={Math.max(line.quantity, line.bundle.available)} onChange={(q) => updateBundleQuantity(line.bundleId, q)} />
                      <span className="price">{formatUzs(line.lineTotalUzs, locale)}</span>
                    </div>
                  </div>
                </div>
              ))}
              {linesWithProducts.map((line) => (
                <div key={`${line.productId}-${line.variantId}`} className="cart-line">
                  <div className="cart-line__img" style={{ background: `var(--tone-${toneFor(line.product.id)})` }}>
                    <ProductImage src={line.product.images[0]} alt={line.product.name} category={line.product.category} className="product-card__image" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <button className="cart-line__title" onClick={() => navigate({ name: "product", slug: line.product.slug })}>
                      {line.product.name}
                    </button>
                    <div className="cart-line__meta">{line.variant.label !== "Стандарт" ? line.variant.label : categoryLabel(line.product, t)}</div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <QuantityStepper
                        quantity={line.quantity}
                        max={line.variant.stock}
                        onChange={(q) => (q <= 0 ? removeItem(line.productId, line.variantId) : updateQuantity(line.productId, line.variantId, q))}
                      />
                      <span className="price">{formatUzs(line.lineTotalUzs, locale)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <AddOnRail />

            <PromoBox error={promoError} />

            <div className="total-row" style={{ marginTop: "var(--space-4)" }}>
              <span>{t("cart.subtotal")}</span>
              <span>{formatUzs(subtotalUzs, locale)}</span>
            </div>
            {goodsDiscount > 0 && (
              <div className="total-row total-row--discount">
                <span>{t("cart.discount")}</span>
                <span>−{formatUzs(goodsDiscount, locale)}</span>
              </div>
            )}
            {preview?.type === "free_delivery" && (
              <div className="total-row total-row--discount">
                <span>{t("checkout.deliveryLine")}</span>
                <span>{t("cart.promoDesc.free_delivery")}</span>
              </div>
            )}
            <div className="total-row total-row--grand">
              <span>{t("cart.total")}</span>
              <span>{formatUzs(Math.max(0, subtotalUzs - goodsDiscount), locale)}</span>
            </div>
          </>
        )}
        <MainAction text={t("cart.checkout")} onClick={() => navigate({ name: "checkout" })} hidden={isEmpty} />
      </main>
      <BottomNav />
    </>
  );
}
